import api from "../index";

// Existing type definitions...

// ** Type Definitions ** //
/**
 * Represents the distribution metrics for each sender in a campaign
 */
interface SenderDistribution {
  senderEmail: string;
  emailCount: number;
  duration: number;
  emailsPerHour: number;
}

/**
 * Information about the next scheduled email in the queue
 */
interface NextScheduledEmail {
  senderEmail: string;
  senderIndex: number;
  scheduledTime: string;
  emailNumber: number;
}

/**
 * Campaign performance and timing metrics
 */
interface CalculatedMetrics {
  senderCount: number;
  minutesBetweenEmails: number;
  emailsPerSender: number;
  totalDuration: number;
  emailsPerHour: number;
  estimatedCompletion: string | null;
  progress: number;
  sent: number;
  timeRemaining: number | null;
  senderDistribution: SenderDistribution[];
  nextScheduledEmails: NextScheduledEmail[];
}

/**
 * Core campaign information
 */
interface Campaign {
  _id: string;
  name: string;
  status:
    | "draft"
    | "scheduled"
    | "sending"
    | "paused"
    | "completed"
    | "stopped";
  scheduledFor?: string;
  totalEmails: number;
  fromName: string;
  subject: string;
  unsubscribeLink: string;
  createdAt: string;
  updatedAt: string;
  calculatedMetrics: CalculatedMetrics;
}

/**
 * Campaign statistics summary
 */
interface CampaignSummary {
  totalCampaigns: number;
  scheduledCampaigns: number;
  sendingCampaigns: number;
  draftCampaigns: number;
  completedCampaigns: number;
  pausedCampaigns: number;
  stoppedCampaigns: number;
  totalEmails: number;
  activeSenders: number;
}

/**
 * Response format for minimal campaign data
 */
interface MinimalCampaignResponse {
  success: boolean;
  summary: CampaignSummary;
  campaigns: Campaign[];
  groupedCampaigns: {
    scheduled: Campaign[];
    sending: Campaign[];
    draft: Campaign[];
    paused: Campaign[];
  };
  totalCount: number;
  stats: {
    totalCampaigns: number;
    activeCampaigns: number;
    completedCampaigns: number;
    pausedCampaigns: number;
    stoppedCampaigns: number;
  };
}

/**
 * Campaign progress tracking information
 */
interface CampaignProgress {
  campaignId: string;
  name: string;
  status: string;
  progress: {
    sent: number;
    delivered: number;
    failed: number;
    total: number;
    percentage: string;
  };
  sendingRate: {
    configured: number;
    current: number;
    totalSenders: number;
    effectiveRate: number;
  };
  timing: {
    estimatedTimeRemaining: number | null;
    estimatedCompletionTime: string | null;
    scheduledFor: string | null;
  };
  recentActivity: {
    lastFiveMinutes: number;
  };
  timestamp: string;
}

/**
 * Response format for campaign action (pause/resume/stop)
 */
interface CampaignActionResponse {
  message: string;
  campaignId: string;
  status: "sending" | "paused" | "stopped" | "completed";
  sentEmails?: number;
  totalEmails?: number;
  remainingEmails?: number;
}

/**
 * Parameters for sent emails query
 */
interface GetSentEmailsParams {
  page?: number;
  perPage?: number;
  status?: "success" | "failed";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  searchText?: string;
  dateFrom?: string;
  dateTo?: string;
  domain?: any;
}

/**
 * Parameters for updating sent email status
 */
interface UpdateSentEmailStatusParams {
  status: "success" | "failed";
  error?: string;
}

/**
 * Parameters for dashboard statistics
 */
interface DashboardStatsParams {
  period?: string;
  dateFrom?: string;
  dateTo?: string;
  campaignId?: string;
}

// ** Campaign Management API ** //
/**
 * Creates a new email campaign
 * @param values Campaign configuration data
 */
export const createEmailCampaign = (values: any) => {
  return api.post("/email-campaign", values);
};

/**
 * Retrieves all email campaigns
 */
export const getEmailCampaigns = () => {
  return api.get("/email-campaigns");
};

/**
 * Gets paginated email campaigns with optional filters
 * @param page Page number
 * @param perPage Items per page
 * @param searchText Optional search term
 * @param status Optional status filter
 */
export const getEmailCampaignsPaginated = async (
  page: number,
  perPage: number,
  searchText?: string,
  status?: string
) => {
  const params = new URLSearchParams({
    page: String(page),
    perPage: String(perPage),
  });

  if (searchText) params.append("searchText", searchText);
  if (status) params.append("status", status);

  return await api.get(`/email-campaigns/paginated?${params.toString()}`);
};

/**
 * Gets a specific campaign by ID
 * @param id Campaign ID
 */
export const getEmailCampaignById = (id: string) => {
  return api.get(`/email-campaign/${id}`);
};

/**
 * Updates an existing campaign
 * @param id Campaign ID
 * @param values Updated campaign data
 */
export const updateEmailCampaignById = (id: string, values: any) => {
  return api.put(`/email-campaign/${id}`, values);
};

/**
 * Deletes a campaign
 * @param id Campaign ID
 */
export const deleteEmailCampaign = (id: string) => {
  return api.delete(`/email-campaign/${id}`);
};

/**
 * Gets campaign metadata
 */
export const getEmailCampaignMetadata = () => {
  return api.get(`/email-campaign/campaign/get-metadata`);
};

/**
 * Initiates sending of a campaign
 * @param id Campaign ID
 */
export const sendEmailCampaign = (id: string) => {
  return api.post(`/email-campaign/${id}/bulk-send`);
};

/**
 * Pauses an active email campaign
 * @param id Campaign ID
 * @returns Promise with pause operation result
 */
export const pauseEmailCampaign = (
  id: string
): Promise<CampaignActionResponse> => {
  return api.post(`/email-campaign/${id}/pause`);
};

/**
 * Resumes a paused email campaign
 * @param id Campaign ID
 * @returns Promise with resume operation result
 */
export const resumeEmailCampaign = (
  id: string
): Promise<CampaignActionResponse> => {
  return api.post(`/email-campaign/${id}/resume`);
};

/**
 * Stops an email campaign permanently
 * @param id Campaign ID
 * @returns Promise with stop operation result
 */
export const stopEmailCampaign = (
  id: string
): Promise<CampaignActionResponse> => {
  return api.post(`/email-campaign/${id}/stop`);
};

// ** Email Tracking API ** //
/**
 * Gets paginated sent emails with filters
 * @param id Campaign ID
 * @param params Query parameters
 */
export const getSentEmailsPaginated = (
  id: string,
  params: GetSentEmailsParams = {}
) => {
  const queryParams = new URLSearchParams({
    page: String(params.page || 1),
    perPage: String(params.perPage || 10),
    sortBy: params.sortBy || "sentAt",
    sortOrder: params.sortOrder || "desc",
  });

  if (params.status) queryParams.append("status", params.status);
  if (params.searchText) queryParams.append("searchText", params.searchText);
  if (params.dateFrom) queryParams.append("dateFrom", params.dateFrom);
  if (params.dateTo) queryParams.append("dateTo", params.dateTo);
  if (params.domain) queryParams.append("domain", params.domain);

  return api.get(`/email-campaign/${id}/sent-emails?${queryParams.toString()}`);
};

/**
 * Updates the status of a sent email
 * @param campaignId Campaign ID
 * @param emailId Email ID
 * @param params Status update parameters
 */
export const updateSentEmailStatus = (
  campaignId: string,
  emailId: string,
  params: UpdateSentEmailStatusParams
) => {
  return api.put(
    `/email-campaign/${campaignId}/sent-emails/${emailId}`,
    params
  );
};

/**
 * Gets sent emails statistics
 * @param campaignId Campaign ID
 */
export const getSentEmailsStats = (campaignId: string) => {
  return api.get(`/email-campaign/${campaignId}/sent-emails/stats`);
};

// ** Analytics API ** //
/**
 * Gets overall campaign analytics
 * @param id Campaign ID
 */
export const getCampaignAnalytics = (id: string) => {
  return api.get(`/email-campaign/${id}/analytics`);
};

/**
 * Gets detailed open analytics
 * @param id Campaign ID
 */
export const getCampaignOpenAnalytics = (id: string) => {
  return api.get(`/email-campaign/${id}/analytics/opens`);
};

/**
 * Gets detailed click analytics
 * @param id Campaign ID
 */
export const getCampaignClickAnalytics = (id: string) => {
  return api.get(`/email-campaign/${id}/analytics/clicks`);
};

/**
 * Gets engagement timeline
 * @param id Campaign ID
 */
export const getCampaignEngagementTimeline = (id: string) => {
  return api.get(`/email-campaign/${id}/analytics/timeline`);
};

/**
 * Gets geographical analytics data
 * @param id Campaign ID
 */
export const getCampaignGeographicalData = (id: string) => {
  return api.get(`/email-campaign/${id}/analytics/geography`);
};

/**
 * Gets device analytics
 * @param id Campaign ID
 */
export const getCampaignDeviceAnalytics = (id: string) => {
  return api.get(`/email-campaign/${id}/analytics/devices`);
};

// ** Dashboard API ** //
/**
 * Gets dashboard statistics
 * @param params Dashboard query parameters
 */
export const fetchDashboardStats = (params: DashboardStatsParams) => {
  const queryParams = new URLSearchParams();

  if (params.period) queryParams.append("period", params.period);
  if (params.dateFrom) queryParams.append("dateFrom", params.dateFrom);
  if (params.dateTo) queryParams.append("dateTo", params.dateTo);
  if (params.campaignId) queryParams.append("campaignId", params.campaignId);

  const url =
    "/email-campaigns/dashboard-stats" +
    (queryParams.toString() ? `?${queryParams.toString()}` : "");

  return api.get(url);
};

/**
 * Gets minimal campaign data with enhanced details
 */
export const getMinimalCampaignData = (): Promise<MinimalCampaignResponse> => {
  return api.get("/email-campaigns/minimal");
};

/**
 * Gets real-time campaign progress
 * @param campaignId Campaign ID
 */
export const getCampaignProgress = (
  campaignId: string
): Promise<CampaignProgress> => {
  return api.get(`/email-campaign/${campaignId}/progress`);
};
